import React, { useEffect, useMemo } from 'react';
import { Row, Col, Typography, Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';

import { FormValue } from '../useForm';
import useQueryUrl from 'src/hooks/useQueryUrl';
import { SelectField } from 'src/components/form';
import useProductCategory from 'src/queries/useProductCategory';
import { checkChangeLanguage } from 'src/utils/helper';
import { useLocation } from 'react-router-dom';
import { BLANK_OPTION, CATEGORY_TYPE } from 'src/constants/app';

type Props = {
  formInfo: UseFormReturn<FormValue, any>;
  dataReEstimate?: Response.EstimatePriceDetail['item'];
  dataCategoryPage?: SearchParams.CategoryProductSearch;
  prevLang: string;
  lang: string;
  isProductFinished?: boolean;
  setSearchKeywordProduct: (item: string) => void;
  resetInputData: () => void;
  setProduct: (e) => void;
};

const CategoriesList: React.FC<Props> = ({
  formInfo,
  dataReEstimate,
  prevLang,
  lang,
  isProductFinished = false,
  setSearchKeywordProduct,
  resetInputData,
  setProduct,
}) => {
  const { control, setValue, watch, getValues, clearErrors } = formInfo;
  const { t } = useTranslation();
  const { categories } = useProductCategory();
  const location = useLocation();
  const params = useQueryUrl<SearchParams.EstimatePriceSearch>();

  const categoriesData = useMemo(
    () =>
      categories &&
      categories.map((item) => ({
        label: item.categoryName || '',
        value: item.categoryId || '',
        childrenList: item.childrenList,
      })),
    [categories.length, JSON.stringify(categories)],
  );

  const varietyValue = watch('variety') ?? '';

  const subCategories = categoriesData
    ?.find((item) => item.value === varietyValue)
    ?.childrenList.map((item) => ({
      label: item.categoryName,
      value: item.categoryId,
    }));

  useEffect(() => {
    params?.hinshL3Cd && setValue('variety', params?.hinshL3Cd);
    params?.keijoL3Cd && setValue('shape', params?.keijoL3Cd);
  }, [params?.hinshL3Cd, params?.keijoL3Cd]);

  useEffect(() => {
    if (!params?.webMitrNo) {
      setValue('product', '');
      setProduct('');
      setSearchKeywordProduct('');
      resetInputData();
    }
  }, [params?.webMitrNo, params?.hinshL3Cd, params?.keijoL3Cd]);

  useEffect(() => {
    if (checkChangeLanguage(prevLang, lang)) {
      // setValue('variety', _.get(categoriesData, '[0].value'));
      // setValue('shape', _.get(subCategories, '[0].value'));
    }
  }, [lang]);

  useEffect(() => {
    if (!location?.search) {
      // setValue('variety', _.get(categoriesData, '[0].value'));
      // setValue('shape', _.get(subCategories, '[0].value'));
    }
  }, [location?.search]);

  const handleOnSelect = (type: string, value?: string) => {
    clearErrors('machiningMethod');
    if (type === CATEGORY_TYPE.METERIAL) {
      const shapeValue = getValues('shape');

      const categoriesDataSelected =
        categoriesData?.find((item) => item.value === value)?.childrenList ??
        [];

      const isShapeValid = categoriesDataSelected.some(
        (item) => item.categoryId === shapeValue,
      );

      if (!isShapeValid) {
        setValue('shape', '');
      }
    }
    setValue('product', '');
    setProduct('');
    setSearchKeywordProduct('');
    resetInputData();
  };

  return (
    <Row className="container_pricing_category">
      <Col xs={24} sm={4} md={4} xl={4} className="mt-17">
        <Typography.Text className="form_label_required">
          {t('pricing_page.category_field.name')}
        </Typography.Text>
      </Col>
      <Col xs={24} sm={19} md={19} lg={19} xl={19}>
        <Row gutter={[10, 0]} className="mt-10">
          <Col xs={12} sm={12} md={12} lg={12} xl={12} className="select_group">
            <SelectField<FormValue>
              labelCol={{
                span: ({ vi: 8, th: 8 } as Record<string, number>)[lang] ?? 6,
              }}
              label={t('pricing_page.category_field.category_label')}
              className="w-full"
              selectProps={{
                options: [
                  BLANK_OPTION,
                  ...(categoriesData.filter(
                    (item) => item && item.value && item.value.trim() !== '',
                  ) || []),
                ],
                placeholder: t('pricing_page.category_field.category_label'),
                notFoundContent: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('pricing_page.category_field.no_data')}
                  />
                ),
                showSearch: true,
                optionFilterProp: 'label',
                onSelect: (value) =>
                  handleOnSelect(CATEGORY_TYPE.METERIAL, value),
              }}
              controller={{
                name: 'variety',
                control,
              }}
            />
          </Col>
          <Col xs={12} sm={12} md={12} lg={12} xl={12} className="select_group">
            <SelectField<FormValue>
              labelCol={{ span: 6 }}
              label={
                isProductFinished
                  ? t('pricing_page.category_field.type')
                  : t('pricing_page.category_field.shape_field')
              }
              className="w-full"
              selectProps={{
                options: [BLANK_OPTION, ...(subCategories || [])],
                placeholder: t('pricing_page.category_field.shape_field'),
                notFoundContent: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={t('pricing_page.category_field.no_data')}
                  />
                ),
                showSearch: true,
                optionFilterProp: 'label',
                onSelect: () => handleOnSelect(CATEGORY_TYPE.SHAPE),
              }}
              controller={{
                name: 'shape',
                control,
              }}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default CategoriesList;
